import { IconChartDots3 } from '@tabler/icons-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { BlockViewProps } from '../../core/types';
import { MermaidDiagramBox } from '../../mermaid/MermaidDiagramBox';

export interface MermaidData {
    chart: string;
    title?: string;
}

export function MermaidBlockView({
    data,
    onDataChange,
    onDelete,
    readOnly
}: BlockViewProps<MermaidData>) {
    const [localData, setLocalData] = useState(data);
    const saveTimeoutRef = useRef<NodeJS.Timeout>();

    // Sync external changes
    useEffect(() => {
        setLocalData(data);
    }, [data]);

    const saveData = useCallback(
        (newData: MermaidData) => {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = setTimeout(() => {
                onDataChange(newData);
            }, 500);
        },
        [onDataChange]
    );

    useEffect(() => {
        return () => clearTimeout(saveTimeoutRef.current);
    }, []);

    const handleChartChange = useCallback(
        (newChart: string) => {
            const newData: MermaidData = { ...localData, chart: newChart };
            setLocalData(newData);
            saveData(newData);
        },
        [localData, saveData]
    );

    const handleDelete = useCallback(() => {
        if (!readOnly) onDelete();
    }, [onDelete, readOnly]);

    const handleAiGenerate = useCallback(
        (mermaidCode: string) => {
            const newData: MermaidData = { ...localData, chart: mermaidCode };
            setLocalData(newData);
            saveData(newData);
        },
        [localData, saveData]
    );

    return (
        <MermaidDiagramBox
            title={localData.title ?? 'Mermaid Diagram'}
            icon={<IconChartDots3 size={24} />}
            chart={localData.chart}
            onChange={readOnly ? () => { /* read-only */ } : handleChartChange}
            onDelete={handleDelete}
            onAiGenerate={readOnly ? undefined : handleAiGenerate}
        />
    );
}
